import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import mergeDeep from "lodash-es/merge";
import { CountByAirStatusModel } from "../../../model/types";
import { GetCardsQuery } from "../../query.types";
import { SavedAirResponseDataDTO, SavedAirResponseDTO } from "../../response.types";
import { OnErrorCallback, OnSettledCallback, OnSuccessCallback, useRequestCallbacks } from "shared/api/lib/api.hooks";
import { ObjectByValuesOfType, PartialBy } from "shared/lib/typeCheck.lib";

import { AirSavedCardModel } from "../../../model/model";
import { savedShipmentService } from "../../api";

type ResetCardsQueryFunc = () => Promise<void>;
type RemoveCardsCacheFunc = VoidFunction;
type UpdateCardsCacheDataParam = Partial<ObjectByValuesOfType<SavedAirResponseDataDTO, number | string>>;
type UpdateCardsCacheFunc = (data: UpdateCardsCacheDataParam, card: Partial<SavedAirResponseDataDTO>) => void;
export type GetAirCardsFunc = (
  params?: GetCardsQuery,
  options?: {
    resetQueryBeforeRequest?: boolean;
    onSuccess?: OnSuccessCallback<AirSavedCardModel[]>;
    onError?: OnErrorCallback<QueryError>;
    onSettled?: OnSettledCallback<AirSavedCardModel[], QueryError>;
  }
) => void;

type QueryParams = Pick<PartialBy<UseQueryOptions, "queryKey">, "queryKey">;
type QueryError = Error;

export type UseSavedCardsQueryReturnType = {
  getCards: GetAirCardsFunc;
  resetCardsQuery: ResetCardsQueryFunc;
  removeCardsCache: RemoveCardsCacheFunc;
  updateCardsCache: UpdateCardsCacheFunc;
  cards: AirSavedCardModel[];
  cardsTotalCount: number | undefined;
  countByStatus: CountByAirStatusModel[] | undefined;
  requestCount: number;
  isCardsSuccess: boolean;
  isCardsError: boolean;
  isCardsLoading: boolean;
  cancelGetCardsRequest: () => Promise<void>;
};

const initialGetCardsParams: GetCardsQuery = {};

const GET_SAVED_CARDS_QUERY_KEY = "get_air_saved_cards_query";

export const useSavedCardsQuery = ({
  queryKey = [GET_SAVED_CARDS_QUERY_KEY],
}: QueryParams = {}): UseSavedCardsQueryReturnType => {
  const queryClient = useQueryClient();
  const [getCardsParams, setGetCardsParams] = useState<GetCardsQuery>(initialGetCardsParams);
  const [enabled, setEnabled] = useState<boolean>(false);

  const requestCountRef = useRef<number>(0);

  const meta = useQuery<SavedAirResponseDTO, QueryError, SavedAirResponseDTO>({
    queryKey,
    queryFn: ({ signal }) => savedShipmentService.getCards(getCardsParams, signal),
    enabled,
    retry: false,
  });

  const { data, isError, isLoading, isSuccess } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<SavedAirResponseDTO, QueryError>(meta);

  const resetCardsQuery: ResetCardsQueryFunc = () => {
    return queryClient.resetQueries({ queryKey });
  };

  const updateCardsCache: UpdateCardsCacheFunc = (filterData, newCardData) => {
    queryClient.setQueryData(
      queryKey,
      queryClient.setQueryData<AxiosResponse<SavedAirResponseDTO> | undefined>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const oldCards = oldData.data.cards;

        const foundCardIndex = oldCards.findIndex((card) => {
          return card.reference === filterData.reference;
        });

        if (foundCardIndex === -1) {
          return oldData;
        }

        const updatedCard = mergeDeep(oldCards[foundCardIndex], newCardData);

        const oldCardsCopy = oldCards.slice();
        oldCardsCopy.splice(foundCardIndex, 1, updatedCard);

        return {
          ...oldData,
          data: {
            ...oldData.data,
            cards: oldCardsCopy,
          },
        };
      })
    );
  };

  const getCards = useCallback<GetAirCardsFunc>(
    (params = initialGetCardsParams, { resetQueryBeforeRequest = true, onSuccess, onError, onSettled } = {}) => {
      setRequestCallbacks({
        onSuccess: (response) => {
          onSuccess?.(response.cards.map((card) => new AirSavedCardModel(card)));
        },
        onError,
        onSettled: (response, error) => {
          setEnabled(false);
          requestCountRef.current++;
          onSettled?.(response?.cards ? response.cards.map((card) => new AirSavedCardModel(card)) : [], error);
        },
      });

      if (resetQueryBeforeRequest) {
        resetCardsQuery().finally(() => {
          setGetCardsParams(params);
          setEnabled(true);
        });
        return;
      }

      setGetCardsParams(params);
      setEnabled(true);
    },
    []
  );

  const mappedData = useMemo<AirSavedCardModel[]>(() => {
    if (!data) return [];
    return data.cards.map((data) => new AirSavedCardModel(data));
  }, [data]);

  const cancelGetCardsRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  const removeCardsCache = () => {
    queryClient.removeQueries({ queryKey });
  };

  return {
    resetCardsQuery,
    removeCardsCache: removeCardsCache,
    updateCardsCache,
    getCards,
    cards: mappedData,
    cardsTotalCount: data?.total_count,
    countByStatus: data?.count_by_status,
    requestCount: requestCountRef.current,
    isCardsSuccess: isSuccess,
    isCardsError: isError,
    isCardsLoading: isLoading,
    cancelGetCardsRequest,
  };
};
